import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Upload } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { memorialsApi, formatApiError } from "@/lib/api";
import { STAR_COLOR_OPTIONS } from "@/lib/starColors";
import { compressImage } from "@/lib/imageCompress";

export default function MemorialEdit() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [newImage, setNewImage] = useState(null);
    const [newImagePreview, setNewImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        memorialsApi
            .getByToken(token)
            .then(async (d) => {
                setDoc(d);
                if (d.has_image) {
                    try {
                        const img = await memorialsApi.image(d.slug);
                        setPreviewSrc(img?.data || null);
                    } catch {}
                }
            })
            .catch((e) => setErr(formatApiError(e?.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [token]);

    function set(k, v) {
        setDoc((d) => ({ ...d, [k]: v }));
    }

    async function onFile(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 25 * 1024 * 1024) {
            return toast.error("Bild zu groß (max 25 MB)");
        }
        try {
            const compressed = await compressImage(f);
            setNewImage(compressed);
            if (newImagePreview) URL.revokeObjectURL(newImagePreview);
            setNewImagePreview(URL.createObjectURL(compressed));
        } catch {
            setNewImage(f);
            if (newImagePreview) URL.revokeObjectURL(newImagePreview);
            setNewImagePreview(URL.createObjectURL(f));
        }
    }

    async function save(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const fd = new FormData();
            ["name", "story", "death_date", "location", "message", "owner_name", "star_color"].forEach((k) => {
                if (doc[k] !== undefined && doc[k] !== null) fd.append(k, String(doc[k]));
            });
            if (newImage) fd.append("image", newImage);
            await memorialsApi.updateByToken(token, fd);
            toast.success("Änderungen gespeichert");
            setNewImage(null);
            if (newImagePreview) {
                URL.revokeObjectURL(newImagePreview);
                setNewImagePreview(null);
            }
            const fresh = await memorialsApi.getByToken(token);
            setDoc(fresh);
            if (fresh.has_image) {
                try {
                    const img = await memorialsApi.image(fresh.slug);
                    setPreviewSrc(img?.data || null);
                } catch {}
            }
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    async function remove() {
        if (!window.confirm("Diese Gedenkseite wirklich endgültig löschen?\n\nWichtig: Die Zahlung wird laut AGB §3a nicht erstattet.")) return;
        try {
            await memorialsApi.deleteByToken(token);
            toast.success("Gedenkseite gelöscht");
            navigate("/taubenhimmel");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    if (loading) {
        return (
            <SiteLayout>
                <div className="max-w-3xl mx-auto px-4 py-24 text-[#94a3b8] label gentle-pulse">Wird geladen…</div>
            </SiteLayout>
        );
    }
    if (err || !doc) {
        return (
            <SiteLayout>
                <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="memorial-edit-error">
                    <h1 className="font-display text-3xl mb-4">Link ungültig oder abgelaufen</h1>
                    <p className="text-[#94a3b8] mb-8">{err || "Bitte fordere einen neuen Bearbeitungs-Link an."}</p>
                    <Link to="/taubenhimmel" className="btn-secondary inline-flex">
                        <ArrowLeft size={14} /> Zurück
                    </Link>
                </div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link to={`/taubenhimmel/${doc.slug}`} className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-10">
                    <ArrowLeft size={14} /> Öffentliche Gedenkseite ansehen
                </Link>
                <div className="label mb-3">Meine Gedenkseite</div>
                <h1 className="font-display text-4xl sm:text-5xl mb-10">{doc.name}</h1>

                <form onSubmit={save} className="space-y-5" data-testid="memorial-edit-form">
                    <Field label="Name der Taube">
                        <input value={doc.name || ""} onChange={(e) => set("name", e.target.value)} className="input-field" required data-testid="edit-memorial-name" />
                    </Field>
                    <Field label="Kurze Geschichte">
                        <textarea value={doc.story || ""} onChange={(e) => set("story", e.target.value)} className="input-field" rows={5} data-testid="edit-memorial-story" />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Datum">
                            <input type="date" value={doc.death_date || ""} onChange={(e) => set("death_date", e.target.value)} className="input-field" />
                        </Field>
                        <Field label="Ort">
                            <input value={doc.location || ""} onChange={(e) => set("location", e.target.value)} className="input-field" />
                        </Field>
                    </div>
                    <Field label="Persönliche Nachricht">
                        <textarea value={doc.message || ""} onChange={(e) => set("message", e.target.value)} className="input-field" rows={3} />
                    </Field>
                    <Field label="Dein Name">
                        <input value={doc.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} className="input-field" />
                    </Field>

                    <Field label="Stern-Farbe">
                        <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                            {STAR_COLOR_OPTIONS.map((c) => (
                                <button
                                    type="button"
                                    key={c.key}
                                    onClick={() => set("star_color", c.key)}
                                    title={c.label}
                                    className={`group flex flex-col items-center gap-2 p-2 rounded-xl transition-all ${
                                        doc.star_color === c.key ? "bg-white/5 ring-1 ring-[#f0c674]" : "hover:bg-white/5"
                                    }`}
                                    data-testid={`edit-color-${c.key}`}
                                >
                                    <span
                                        className="w-7 h-7 rounded-full gentle-pulse"
                                        style={{ background: c.hex, boxShadow: `0 0 14px ${c.hex}B3, 0 0 4px ${c.hex}` }}
                                    />
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#94a3b8] group-hover:text-[#f8fafc]">
                                        {c.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </Field>

                    <div className="glass p-5">
                        <div className="label mb-3">Foto</div>
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                            {newImagePreview ? (
                                <img src={newImagePreview} alt="Neu" className="w-24 h-24 rounded-xl object-cover ring-1 ring-[#f0c674]" />
                            ) : previewSrc ? (
                                <img src={previewSrc} alt="Aktuell" className="w-24 h-24 rounded-xl object-cover" />
                            ) : null}
                            <div className="flex-1 min-w-[200px]">
                                <label htmlFor="edit-image" className="cursor-pointer flex items-center gap-3 input-field hover:border-[#f0c674]/40">
                                    <Upload size={16} className="text-[#f0c674]" />
                                    <span className="text-sm text-[#94a3b8]">
                                        {newImage ? "Neues Bild ausgewählt" : "Bild ersetzen (optional)"}
                                    </span>
                                </label>
                                <input id="edit-image" type="file" accept="image/*" onChange={onFile} className="hidden" />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 pt-4">
                        <button type="submit" disabled={submitting} className="btn-primary" data-testid="memorial-edit-save">
                            <Save size={14} /> {submitting ? "Speichert…" : "Speichern"}
                        </button>
                        <button type="button" onClick={remove} className="btn-ghost text-rose-300" data-testid="memorial-edit-delete">
                            <Trash2 size={14} /> Gedenkseite löschen
                        </button>
                    </div>
                    <p className="text-xs text-[#64748b] pt-2">
                        Hinweis: Eine Löschung führt laut AGB §3a nicht zu einer Rückerstattung.
                    </p>
                </form>
            </section>
        </SiteLayout>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-sm text-[#94a3b8] mb-2">
                {label} {required && <span className="text-[#f0c674]">*</span>}
            </span>
            {children}
        </label>
    );
}
